<template>
	<a
		v-if="isExternalLink"
		:href="to"
		target="_blank"
		:class="classes"
		role="button"
		data-testid="button">
		<font-awesome-icon v-if="icon" :icon="['fas', icon]" class="mr-1 text-inherit" />

		<slot />
	</a>

	<router-link
		v-else-if="to && !disabled"
		:to="to"
		:disabled
		:class="classes"
		role="button"
		data-testid="button">
		<font-awesome-icon v-if="icon" :icon="['fas', icon]" class="mr-1 text-inherit" />

		<slot></slot>
	</router-link>

	<button
		v-else
		:disabled="disabled"
		:class="classes"
		role="button"
		data-testid="button"
		@click="onClick">
		<font-awesome-icon v-if="icon" :icon="['fas', icon]" class="mr-1 text-inherit" />
		<slot></slot>
	</button>
</template>

<script setup lang="ts">
	import { computed } from 'vue'

	const props = withDefaults(
		defineProps<{
			icon?: string
			size?: 'x-small' | 'small' | 'medium' | 'large'
			theme?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'outline'
			group?: 'left' | 'right'
			to?: string
			disabled?: boolean
		}>(),
		{
			to: undefined,
			icon: undefined,
			size: 'medium',
			theme: 'primary',
			group: undefined,
			disabled: false
		}
	)

	const classes = computed(() => ({
		'px-3 py-2 focus:ring-2 outline-none': true,
		rounded: !props.group,
		'rounded-l': props.group == 'left',
		'rounded-r -ml-px': props.group == 'right',
		'bg-mabey-950 hover:bg-mabey-500 focus:ring-mabey-700 text-white': props.theme == 'primary',
		'bg-cyan-400 hover:bg-mabey-300 focus:ring-mabey-300 text-mabey-950':
			props.theme == 'secondary',
		'bg-green-500 hover:bg-green-300 focus:ring-green-300 text-white': props.theme == 'success',
		'bg-mabey-600 hover:bg-mabey-300 focus:ring-mabey-300 text-white hover:text-mabey-950':
			props.theme == 'info',
		'bg-yellow-500 hover:bg-yellow-300 focus:ring-yellow-300 text-white hover:text-yellow-900':
			props.theme == 'warning',
		'bg-red-600 hover:bg-red-500 focus:ring-red-500 text-white': props.theme == 'danger',
		'bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50 text-gray-900':
			props.theme == 'outline',
		'px-4 py-3': props.size === 'large',
		'px-2 py-0.5 text-xs': props.size === 'small',
		'px-[8px] pt-[5px] pb-[3px] text-[11px] leading-normal min-h-[25px]':
			props.size === 'x-small',
		'text-sm': props.size != 'x-small',
		'opacity-20 cursor-not-allowed': props.disabled
	}))

	const isExternalLink = computed(() => {
		return (
			typeof props.to === 'string' &&
			(props.to.startsWith('http') || props.to.startsWith('tel'))
		)
	})

	const emit = defineEmits(['click'])

	const onClick = (event: MouseEvent) => {
		emit('click', event)
	}
</script>
